import { Permission } from '@up2stay/up2promo-api-client';
import { Button, cn } from '@up2stay/up2promo-ui';
import { Sparkles } from 'lucide-react';

import { TRIAL_DAYS } from '@/core/billing/plan-catalog';
import { canPurchaseBillingInApp } from '@/core/billing/can-purchase-billing-in-app';
import { ShellLink } from '@/core/components/navigation/shell-link';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';

import { resolveSidebarUpgradeTeaserVariant } from './resolve-sidebar-upgrade-teaser-variant';
import { shouldShowSidebarUpgradeTeaser } from './should-show-sidebar-upgrade-teaser';

type PlanUpgradeTeaserProps = {
  className?: string;
  /**
   * Sidebar footer: drop the benefit list below ~1000px viewport height so
   * the card does not push nav entries out of view (DEV-535).
   */
  compactOnShortViewport?: boolean;
};

/**
 * Compact Plus / Plus–Teams upgrade card for free, trial, and unpaid
 * workspaces. Shared by the desktop sidebar footer and Cap/mobile More hub.
 * Hidden without BILLING_MANAGE, when paid / grant-covered, or on Cap native
 * (web-only billing).
 */
export function PlanUpgradeTeaser({
  className,
  compactOnShortViewport = false,
}: PlanUpgradeTeaserProps) {
  const dict = useDictionary();
  const { can } = usePermissions();
  const { plan, status, isLoading, hasActiveBillingGrant } = useEntitlements();
  const teaserDict = dict.entitlements.upgrade.sidebarTeaser;

  if (
    !canPurchaseBillingInApp() ||
    !shouldShowSidebarUpgradeTeaser({
      canManageBilling: can(Permission.BILLING_MANAGE),
      isLoading,
      plan,
      status,
      hasActiveBillingGrant,
    }) ||
    !plan
  ) {
    return null;
  }

  const variant = resolveSidebarUpgradeTeaserVariant({ plan, status });
  const copy = teaserDict[variant];
  const title = copy.title.replace('{days}', String(TRIAL_DAYS));

  return (
    <div
      data-slot="plan-upgrade-teaser"
      data-variant={variant}
      className={cn(
        'flex flex-col gap-3 rounded-xl border border-[color:var(--ghost-accent-border)]',
        'bg-[color:var(--ghost-accent-bg)]/55 p-3.5',
        'dark:border-primary/30 dark:bg-primary/10',
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            'bg-primary/15 text-primary',
          )}
        >
          <Sparkles className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug text-foreground">
            {title}
          </p>
          <ul
            className={cn(
              'mt-1.5 flex flex-col gap-1',
              compactOnShortViewport && '[@media(max-height:1000px)]:hidden',
            )}
          >
            {copy.benefits.map((benefit) => (
              <li
                key={benefit}
                className="flex gap-1.5 text-xs leading-snug text-muted-foreground"
              >
                <span
                  className="mt-1.5 size-1 shrink-0 rounded-full bg-primary"
                  aria-hidden
                />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Button
        variant="primary"
        size="sm"
        radius="md"
        className="h-8 w-full text-xs font-semibold"
        asChild
      >
        <ShellLink to={shellPaths.moreWorkspaceBillingUpgrade}>
          {copy.cta}
        </ShellLink>
      </Button>
    </div>
  );
}
